import { opsMissionTypeEnum, property, targetPlatformEnum } from "../dataStruct";
import {
  checkPointIsRouteRelevant,
  getParameterOfArray,
  getParametersArray,
  getUtcTime,
  markDownProcessing,
  padLeft,
} from "../functions";
import { navLegStruct } from "./navLogReport";
import { fppProvider } from "./providers/fppProvider";

export class metarReport {
  async createMetMarkdown(flightId: string, classCom: any, navLog: navLegStruct[]): Promise<string> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    // Here is the header part
    // =======================

    const mdText_lst: string[] = [];

    mdText_lst.push("## MET REPORTS");
    mdText_lst.push(
      "Flight **{FlightNumber}** -  Ops Mission Type: **{opsMissionType}** - revision {revision} @  {issueTime} \n"
    );
    mdText_lst.push(
      "ETD **{estTimeDep_gui_UTC} / {estTimeDep_gui_LT}**:   **{originName}** to **{destName}**,  Alternate {altName}\n"
    );
    mdText_lst.push("_____________");

    // replace values — catalog-dispatched
    const flightNumber = (await classCom.namedQuery("flightlistByFlightId", { flightId }))[0].flightNumber;
    const revision: string = String((await classCom.namedQuery("fuelCountByFlightId", { flightId }))[0].n);
    const utcTimeNow = getUtcTime(new Date());
    const issueTime =
      padLeft(utcTimeNow.getHours().toString(), "0", 2) +
      ":" +
      padLeft(utcTimeNow.getMinutes().toString(), "0", 2) +
      " UTC";
    const estTimeDep: Date = new Date(
      Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
    );
    const estTimeDep_gui_LT =
      padLeft(estTimeDep.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep.getMinutes().toString(), "0", 2) +
      " LT";
    const estTimeDep_utc = getUtcTime(estTimeDep);
    const estTimeDep_gui_UTC =
      padLeft(estTimeDep_utc.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep_utc.getMinutes().toString(), "0", 2) +
      " UTC";

    const originIcao: string = getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams)[0]
      .value;
    const destIcao: string = getParameterOfArray(flightId, "flightlist", property.icaoDest, flightlistParams)[0].value;
    const altIcao: string = getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams)[0]
      .value;

    // Here is the content part
    // ========================

    const metarToolid: number = 122768313; // this is a temporary solution, until the weathermodel is redone

    const metarreports = await this.metarsAlongRoute(metarToolid, navLog, classCom, 10000);

    for (let i = 0; i < metarreports.length; i++) {
      const message = this.formatMetRow(
        metarreports[i].geoPos,
        metarreports[i].feature.effectiveFrom,
        metarreports[i].feature.value
      );

      if (!mdText_lst.includes(message)) {
        mdText_lst.push(message);
      }
    }

    let mdString: string = "";
    for (let i = 0; i < mdText_lst.length; i++) {
      mdString += mdText_lst[i] + "\n";
    }

    mdString = mdString.replace("{FlightNumber}", flightNumber);
    mdString = mdString.replace("{opsMissionType}", opsMissionTypeEnum[opsMissionTypeEnum.all].toUpperCase());
    mdString = mdString.replace("{revision}", revision);
    mdString = mdString.replace("{issueTime}", issueTime);
    mdString = mdString.replace("{estTimeDep_gui_UTC}", estTimeDep_gui_UTC);
    mdString = mdString.replace("{estTimeDep_gui_LT}", estTimeDep_gui_LT);
    mdString = mdString.replace("{originName}", originIcao);
    mdString = mdString.replace("{destName}", destIcao);
    mdString = mdString.replace("{altName}", altIcao);

    return mdString;
  }

  /**
   * The METAR features in each leg's box, leg by leg, within `corridorWidth / 2` of the leg.
   * Moved here from briefingFlowFeatures.geoContentQuery when the briefing card
   * stopped using it; this report is the only caller.
   * ponytail: checkPointIsRouteRelevant measures in NM, so the caller's 10000 filters
   * nothing and the box alone limits the result. That is the old behaviour, kept.
   */
  private async metarsAlongRoute(
    toolId: number,
    legs: navLegStruct[],
    classCom: any,
    corridorWidth: number
  ): Promise<{ geoPos: number[]; feature: any }[]> {
    const found: { geoPos: number[]; feature: any }[] = [];

    // The reads of all legs start together: on web each one is a round trip.
    const rowsPerLeg: any[][] = await Promise.all(
      legs.slice(1).map((leg, i) => {
        const [x1, y1] = legs[i].geoPos;
        const [x2, y2] = leg.geoPos;
        // The leg's bounding box, inflated by half a degree.
        return classCom.namedQuery("toolFeaturesInBoundsByKey", {
          minX: Math.min(x1, x2) - 0.5,
          maxX: Math.max(x1, x2) + 0.5,
          minY: Math.min(y1, y2) - 0.5,
          maxY: Math.max(y1, y2) + 0.5,
          toolId,
          key: "html",
        });
      })
    );

    for (let legId = 1; legId < legs.length; legId++) {
      const [x1, y1] = legs[legId - 1].geoPos;
      const [x2, y2] = legs[legId].geoPos;

      for (const q of rowsPerLeg[legId - 1]) {
        // Leave out features with a huge extent: the briefing shows those statically.
        if (!(Math.abs((q.maxX - q.minX) * (q.maxY - q.minY)) < 1000)) continue;

        const geoPos = [(q.minX + q.maxX) / 2, (q.minY + q.maxY) / 2];
        const { distFromRoute } = checkPointIsRouteRelevant([x1, y1], [x2, y2], geoPos, true);
        if (distFromRoute < corridorWidth / 2) found.push({ geoPos, feature: q });
      }
    }
    return found;
  }

  formatMetRow(location: number[], publishDate: number, message: string) {
    message = message.replace("/./g", ". <br>");

    let startDate_utc: string = getUtcTime(new Date(publishDate)).toLocaleTimeString() + " UTC";

    // datestamp not coorect on app, guess the background process messes something up
    startDate_utc = "";

    const locationString: string = "lon: " + round(location[0], 3) + " lat: " + round(location[1], 3);

    let mdString: string =
      "| {location}     {publishDate}   |\n" +
      "| --                           |\n" +
      "| **{message}**                |\n";

    mdString = mdString.replace("{location}", locationString);
    mdString = mdString.replace("{publishDate}", startDate_utc);
    mdString = mdString.replace("{message}", message);

    return mdString;
  }

  async getMetReport(flightId: string, classCom: any, returnMarkdown: boolean = false): Promise<string> {
    const result = await classCom.namedQuery("metProcessReportByFlight", { flightId });

    const mdp = new markDownProcessing();

    // A flight can have no weather report row yet. Then the report says so,
    // so that neither the page nor the OFP text shows a silent gap.
    const report: string = result?.[0]?.processReport ?? "## MET REPORTS\nNo weather report for this flight.\n";

    if (returnMarkdown) {
      return report;
    }

    const html: string = await mdp.convMarkdown(
      report,
      classCom.appearance,
      flightId,
      opsMissionTypeEnum.all,
      classCom,
      new fppProvider(),
      // No row: "" renders the text unsigned. It was never calculated under a signature.
      result?.[0] ? result[0].signatureId : ""
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }

  async getProcessResultState(flightId: string, classCom: any): Promise<string> {
    const result = await classCom.namedQuery("metProcessResultStateByFlight", { flightId });

    if (result.length == 0) {
      return "NIL";
    }

    if (result[0].processResultState == 0) {
      return "N/A";
    }

    if (result[0].processResultState == 1) {
      return "OK";
    }

    if (result[0].processResultState == 2) {
      return "WARN";
    }

    if (result[0].processResultState == 3) {
      return "FAIL";
    }

    return "N/A";
  }
}

export function round(val: number, digits: number): number {
  return Math.round(val * Math.pow(10, digits)) / Math.pow(10, digits);
}
